import React, { useCallback } from 'react'
import { Maybe } from '@graphql-tools/utils'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { CaptionSM, LabelSM, TitleLG } from '@/atoms/Text'
import {
  findTierAndIntervalByBimOfferId,
  GuildOfferPackage,
  GuildOfferPrice,
  PRICE_ANCHOR_EXPERIMENT_VARIANTS,
} from '@/constants/guild'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Offer } from '@/services/PifService'
import { formatPrice } from '@/services/PriceFormatter'
import { getPromotionalPrice, getPromotionMonthlyDuration, GuildSale, ValidatedPromotion } from '@/services/SaleService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { writeToSessionStorage } from '@/utils/session-storage'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildUpsellBannerProps {
  className?: string
  offer: Offer
}

function getPremiumYearlyPrice(sale: Maybe<GuildSale>, offerPackage: GuildOfferPackage) {
  return getAnnualPrice(sale, offerPackage.premium.annual)
}

function getBasicYearlyPrice(sale: Maybe<GuildSale>, standardPrice: number, offerId: string) {
  return getAnnualPrice(sale, { standardPrice, bimOfferId: offerId })
}

function getAnnualPrice(sale: Maybe<GuildSale>, guildOffer: GuildOfferPrice) {
  if (!sale) return guildOffer.standardPrice

  const promotion = sale?.promotions.find((p) => p.offer.id === guildOffer.bimOfferId) as ValidatedPromotion

  if (!promotion) {
    return guildOffer.standardPrice
  } else {
    return getPromotionalPrice(promotion) ?? guildOffer.standardPrice
  }
}

function getPriceAfterPayingTwelveMonths(sale: Maybe<GuildSale>, defaultPrice: number, offerId: string) {
  const defaultPriceForYear = defaultPrice * 12
  if (!sale) return defaultPriceForYear

  const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion

  if (!promotion) {
    return defaultPriceForYear
  } else {
    const promotionMonths = getPromotionMonthlyDuration(promotion.promotionCode)
    const promotionPrice = getPromotionalPrice(promotion)
    if (promotionPrice === null) return defaultPriceForYear

    const discountedMonthsInFirstYear = promotionMonths < 12 ? promotionMonths : 12
    const normalRateMonthsInFirstYear = 12 - promotionMonths > 0 ? 12 - promotionMonths : 0

    return discountedMonthsInFirstYear * promotionPrice + normalRateMonthsInFirstYear * defaultPrice
  }
}

export const GuildUpsellBanner: React.FC<GuildUpsellBannerProps> = ({ className, offer }) => {
  const { t } = useTranslate('guild')
  const { sale, experiments, releaseToPromote } = useGuildSignupContext()
  const { locale } = useLocale()
  const track = useSafeTrack()

  const variant = (experiments['guild_price_anchor'] || 'OFF') as string

  const offerPackage = PRICE_ANCHOR_EXPERIMENT_VARIANTS[variant]

  const releaseTitle = releaseToPromote?.title
  const releaseDateRaw = releaseToPromote?.releaseDate
  const releaseDate = releaseDateRaw
    ? new Date(releaseDateRaw) < new Date()
      ? t('now', 'now')
      : new Date(releaseDateRaw).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : undefined

  const yearlyPriceUnformatted = getPremiumYearlyPrice(sale, offerPackage)
  const yearlyPrice = formatPrice(yearlyPriceUnformatted, {
    locale,
    includeDecimals: false,
    currency: 'USD',
  })

  function getUpsellContent(
    offerPackage: GuildOfferPackage,
    offerId: number,
    t: TranslateFunction,
    releaseTitle?: string | null,
    releaseDate?: string,
    sale?: GuildSale | null,
  ): {
    heading?: string | null
    buttonText?: string | null
    description?: React.ReactNode | null
    href?: string | null
  } {
    //TODO: this will need to use the smart link again when we are running a sale. doing this only for the guild_price_anchor experiment
    const upsellLink = `${paths.guild.checkout}/${offerPackage.premium.annual.bimOfferId}`
    const planDetails = findTierAndIntervalByBimOfferId(offerId + '')

    const getPremiumYearlyFromBasicMonthlyUpsell = () => {
      return {
        heading: t('unlockMoreWithPremium', 'Unlock more with Premium!'),
        buttonText: t('goPremium', 'Go Premium'),
        description: (
          <Translate
            t={t}
            i18nKey="basicYearlyDescriptionBasicUpsellV2"
            values={{ releaseTitle, releaseDate, yearlyPrice }}
          >
            For just <b>{{ yearlyPrice }}/year</b>, you&apos;ll get <b>ad-free streaming</b> and{' '}
            <b>two movie tickets to every Angel theatrical release</b>, including <i>{{ releaseTitle }}</i> in theaters{' '}
            {{ releaseDate }}.
          </Translate>
        ),
        href: upsellLink,
      }
    }

    const getPremiumYearlyFromPremiumMonthlyUpsell = ({
      standardPrice,
      offerId,
    }: {
      standardPrice: number
      offerId: string
    }) => {
      const premiumMonthlyPriceForTwelveMonths = getPriceAfterPayingTwelveMonths(sale, standardPrice, offerId)

      const totalSavings = formatPrice(premiumMonthlyPriceForTwelveMonths - yearlyPriceUnformatted, {
        locale,
        currency: 'USD',
        includeDecimals: false,
      })
      return {
        heading: t('save50MonthlyV1', 'Save {{ totalSavings }} by paying yearly', { totalSavings }),
        buttonText: t('switchToYearly', 'Switch to Yearly'),
        description: (
          <Translate t={t} i18nKey="specialOfferDescriptionV1" values={{ totalSavings, yearlyPrice }}>
            Get the same benefits for just <b>{{ yearlyPrice }}/year</b> (That&apos;s {{ totalSavings }} less than
            paying monthly).
          </Translate>
        ),
        href: upsellLink,
      }
    }

    const getPremiumYearlyFromBasicYearlyUpsell = ({
      standardPrice,
      offerId,
    }: {
      standardPrice: number
      offerId: string
    }) => {
      const basicYearlyPrice = getBasicYearlyPrice(sale, standardPrice, offerId)
      const premiumYearlyPrice = getPremiumYearlyPrice(sale, offerPackage)
      const morePerYear = formatPrice(premiumYearlyPrice - basicYearlyPrice, {
        locale,
        includeDecimals: false,
        currency: 'USD',
      })
      return {
        heading: t('unlockMoreWithPremium', 'Unlock more with Premium!'),
        buttonText: t('goPremium', 'Go Premium'),
        description: (
          <Translate t={t} i18nKey="basicYearlyDescriptionV2" values={{ morePerYear, releaseTitle, releaseDate }}>
            For just <b>{{ morePerYear }} more/year</b>, you&apos;ll enjoy <b>ad-free streaming</b> and get{' '}
            <b>two movie tickets to every Angel theatrical release</b> for an entire year, including{' '}
            <i>{{ releaseTitle }}</i> in theaters {{ releaseDate }}.
          </Translate>
        ),
        href: upsellLink,
      }
    }

    if (planDetails?.interval === 'monthly') {
      if (planDetails?.tier === 'basic') {
        return getPremiumYearlyFromBasicMonthlyUpsell()
      } else if (planDetails?.tier === 'basic-no-ads') {
        return getPremiumYearlyFromBasicMonthlyUpsell()
      } else if (planDetails?.tier === 'premium') {
        return getPremiumYearlyFromPremiumMonthlyUpsell({
          standardPrice: offerPackage.premium.monthly.standardPrice,
          offerId: offerPackage.premium.monthly.bimOfferId,
        })
      }
    } else if (planDetails?.interval === 'annual') {
      if (planDetails?.tier === 'basic') {
        return getPremiumYearlyFromBasicYearlyUpsell({
          standardPrice: offerPackage.basic.annual.standardPrice,
          offerId: offerPackage.basic.annual.bimOfferId,
        })
      } else if (planDetails?.tier === 'basic-no-ads') {
        const basicNoAds = offerPackage.basicNoAds

        if (basicNoAds) {
          return getPremiumYearlyFromBasicYearlyUpsell({
            standardPrice: basicNoAds.annual.standardPrice,
            offerId: basicNoAds.annual.bimOfferId,
          })
        }
      }
    }

    return {
      heading: null,
      buttonText: null,
      description: null,
      href: null,
    }
  }

  const { heading, buttonText, description, href } = getUpsellContent(
    offerPackage,
    Number(offer.id),
    t,
    releaseTitle,
    releaseDate,
    sale,
  )

  const handleUpsellClick: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault()
      if (!href) return
      track('Guild Checkout Upsell Clicked', {
        offer: offer,
        href: href,
        releaseToPromote: releaseToPromote,
      })
      const checkoutUpsellInitiated = {
        date: new Date().toISOString(),
      }
      writeToSessionStorage('checkoutUpsellInitiated', checkoutUpsellInitiated)
      window.location.href = href
    },
    [href, offer, releaseToPromote, track],
  )

  const isSketch = releaseToPromote?.theatricalSlug === slugs.sketch

  if (!heading) return null

  return (
    <div
      className={classNames('mx-4 mb-8 mt-4 rounded-[10px] p-[2.5px] md:mx-8 lg:mx-16 lg:p-[3px]', {
        'bg-sketch-gradient': isSketch,
        'bg-guild-gradient': !isSketch,
      })}
    >
      <div
        className={classNames(
          'p-4 bg-core-gray-900 rounded-lg flex-col lg:flex-row w-full flex lg:justify-between lg:gap-4',
          className,
        )}
      >
        <div className="flex flex-col items-center gap-2 lg:items-start lg:gap-4">
          <TitleLG
            weight="bold"
            className={classNames(
              'lg:photon-heading-xs bg-guild-gradient bg-clip-text text-center text-transparent lg:text-left',
              { 'bg-sketch-gradient': isSketch },
            )}
          >
            {heading}
          </TitleLG>
          <CaptionSM color="core-gray-400" className=" text-center lg:text-left">
            {description}
          </CaptionSM>
          {releaseToPromote?.images?.angelKeyArt1?.image16x9 && (
            <Image
              src={getCloudinaryImageUrl({
                path: releaseToPromote.images.angelKeyArt1.image16x9,
                width: 250,
                height: 140,
                transforms: 'c_fill',
              })}
              alt={`${releaseToPromote?.title} poster`}
              width={250}
              height={140}
              className="mx-auto my-2 rounded-xl lg:hidden"
            />
          )}
          {href && (
            <LinkButton
              shallow={false}
              prefetch={false}
              href={href}
              onClick={handleUpsellClick}
              variant="gray-700"
              className={classNames('w-full border-none bg-black/20 px-5 py-[11px] backdrop-blur md:w-fit', {
                '!bg-sketch-gradient': isSketch,
              })}
            >
              <LabelSM color="white">{buttonText}</LabelSM>
            </LinkButton>
          )}
        </div>
        <div className="lg:min-w-[237px]">
          {releaseToPromote?.images?.angelKeyArt1?.image16x9 && (
            <Image
              src={getCloudinaryImageUrl({
                path: releaseToPromote.images.angelKeyArt1.image16x9,
                width: 237,
                height: 132,
                transforms: 'c_fill',
              })}
              alt={`${releaseToPromote?.title} poster`}
              width={237}
              height={132}
              className="hidden rounded-xl lg:flex"
            />
          )}
        </div>
      </div>
    </div>
  )
}
